/**
 * Persisted competition state — Career, World Cup and squad setups. (§9.1)
 *
 * ── The contract ────────────────────────────────────────────────────────────
 *
 * Three versioned localStorage keys, one per feature, following exactly the
 * pattern settings.ts established:
 *
 *   fg.career.v1    the club, the season, the fixture list, every result
 *   fg.worldcup.v1  the draw, the group results, the knockout bracket
 *   fg.squads.v1    per-team formation + starting XI
 *
 * Parsing NEVER throws and never returns a half-valid object. A blob written by
 * a future client, hand-edited, or truncated by a full quota returns `null`
 * (= "no career") rather than a `CareerState` with an undefined fixture list
 * that explodes three screens later. Writing is equally defensive: quota and
 * private-mode failures must never take the menu down.
 *
 * ── Why the state is this shape ─────────────────────────────────────────────
 *
 * Everything derivable is DERIVED, everything that embodies a decision is
 * STORED. Fixtures are regenerated from the stored `clubIds` + `seed` on every
 * load rather than persisted (a 240-fixture array is dead weight); squads are
 * regenerated by teams.ts from the team id; but the DRAW, the results and the
 * bracket are stored, because they are outcomes and re-rolling them on reload
 * would rewrite the player's history under them.
 *
 * `clubIds` is stored rather than read from teams.ts on load for exactly that
 * reason: if a future session adds a 17th club, an in-progress season keeps its
 * own 16 and its own schedule instead of silently reshuffling.
 *
 * ── How a match result gets back in here ────────────────────────────────────
 *
 * The career/World Cup screens start a match through the EXISTING
 * `onStartPractice` handler, tagging the `MatchSetup` with a
 * `CompetitionContext` (see ui/menu.ts). When the match exits, practice.ts
 * hands main.ts the real scoreline and main.ts calls `reportCompetitionResult`
 * below. The context carries a `token` identifying the exact fixture it started
 * from; if the stored state has moved on (the player reset the career mid-match,
 * or two tabs raced), the token no longer matches and the result is DROPPED
 * rather than written into the wrong fixture.
 *
 * Pure logic here; the DOM lives in career.ts / worldCup.ts / squadScreen.ts.
 */

import {
  type BracketMatch,
  type Fixture,
  type Group,
  type MatchResult,
  type TableRow,
  bracketChampion,
  bracketComplete,
  bracketMatchFor,
  bracketRound,
  bracketRoundCount,
  buildBracket,
  computeTable,
  createRng,
  drawGroups,
  fixtureFor,
  generateFixtures,
  groupRoundCount,
  groupStageFixtures,
  hashString,
  positionInTable,
  qualifiersFromGroups,
  setBracketResult,
  simulateBracketRound,
  simulateResult,
  simulateRound,
} from './competition.js';
import {
  CLUBS,
  DEFAULT_FORMATION_ID,
  NATIONS,
  autoLineup,
  formationById,
  positionFit,
  squadFor,
  teamOrStub,
  teamById,
} from './teams.js';

export const CAREER_KEY = 'fg.career.v1';
export const WORLD_CUP_KEY = 'fg.worldcup.v1';
export const SQUADS_KEY = 'fg.squads.v1';
export const HONOURS_KEY = 'fg.honours.v1';

/** Home and away — 16 clubs → 30 matchdays. */
const LEAGUE_LEGS = 2;
/** The FIELD is 32 → 8 groups of 4 → 16 qualifiers → R16/QF/SF/FINAL. */
const GROUP_COUNT = 8;
/**
 * How many nations actually contest the tournament.
 *
 * The pool (NATIONS) is allowed to be larger, and is: the bracket code needs a
 * power of two, but the picker should not be capped at the same number just to
 * keep the maths tidy. drawGroups fills whole pots and drops the remainder, so
 * a pool bigger than the field would quietly delete its weakest members from
 * every tournament — which is exactly the nation a player is most likely to
 * have picked for the challenge. qualifyField below makes that explicit.
 */
const FIELD_SIZE = GROUP_COUNT * 4;

// ---------------------------------------------------------------------------
// Career
// ---------------------------------------------------------------------------

export interface SeasonRecord {
  season: number;
  /** 1-based finishing position. */
  position: number;
  points: number;
  played: number;
}

export interface CareerState {
  version: 1;
  clubId: string;
  /** Seeds every simulated fixture. Stored so a reload reproduces the season. */
  seed: number;
  /** 1-based. */
  season: number;
  /** 0-based matchday. `round === totalRounds` means the season is complete. */
  round: number;
  /** The league's clubs, frozen at career creation (see the header). */
  clubIds: string[];
  results: MatchResult[];
  history: SeasonRecord[];
}

export function startCareer(clubId: string): CareerState {
  return {
    version: 1,
    clubId,
    seed: hashString(`${clubId}:career`),
    season: 1,
    round: 0,
    clubIds: CLUBS.map((c) => c.id),
    results: [],
    history: [],
  };
}

export function careerFixtures(state: CareerState): Fixture[] {
  return generateFixtures(state.clubIds, LEAGUE_LEGS);
}

export function careerTotalRounds(state: CareerState): number {
  return Math.max(0, (state.clubIds.length - 1) * LEAGUE_LEGS);
}

export function careerSeasonComplete(state: CareerState): boolean {
  return state.round >= careerTotalRounds(state);
}

/** The club's next fixture, or null once the season is over. */
export function nextCareerFixture(state: CareerState): Fixture | null {
  if (careerSeasonComplete(state)) return null;
  return fixtureFor(careerFixtures(state), state.round, state.clubId);
}

export function careerTable(state: CareerState): TableRow[] {
  return computeTable(state.clubIds, state.results);
}

/**
 * Identifies the fixture a running match came from. A career only ever has ONE
 * live fixture (round N of season S), so this is enough to detect a stale
 * result without persisting a "pending" flag that could be orphaned by a crash.
 */
export function careerMatchToken(state: CareerState): string {
  return `career:${state.season}:${state.round}`;
}

function ratingOf(teamId: string): number {
  return teamOrStub(teamId).rating;
}

/** Per-round RNG. Deterministic in (seed, season, round) so the same matchday
 *  simulated twice — e.g. after a failed save and a reload — is the same. */
function roundRng(state: CareerState, round: number): () => number {
  return createRng(state.seed ^ hashString(`s${state.season}r${round}`));
}

/**
 * Advance one matchday. `ourResult` is the scoreline the player actually
 * played, in HOME/AWAY terms; pass null to simulate their fixture too.
 *
 * Every other fixture of the same matchday is simulated in the same call —
 * a table that only ever moves for one club is not a league.
 */
export function advanceCareerRound(
  state: CareerState,
  ourResult: { homeGoals: number; awayGoals: number } | null,
): CareerState {
  if (careerSeasonComplete(state)) return state;
  const fixtures = careerFixtures(state);
  const round = state.round;
  const rng = roundRng(state, round);
  const ours = fixtureFor(fixtures, round, state.clubId);

  const played: MatchResult[] = [];
  if (ours) {
    const score = ourResult ?? simulateResult(ratingOf(ours.home), ratingOf(ours.away), rng);
    played.push({
      round,
      home: ours.home,
      away: ours.away,
      homeGoals: score.homeGoals,
      awayGoals: score.awayGoals,
      played: ourResult !== null,
    });
  }
  const rest = simulateRound(
    fixtures, round, ratingOf, rng,
    (f) => !!ours && f.home === ours.home && f.away === ours.away,
  );

  return {
    ...state,
    round: round + 1,
    results: [...state.results, ...played, ...rest],
  };
}

/**
 * Record the scoreline of a fixture the player actually played. `ourGoals` /
 * `theirGoals` are from the PLAYER'S side — the sim always seats the human as
 * team A, so the venue mapping happens here rather than at every call site.
 */
export function recordCareerResult(
  state: CareerState,
  ourGoals: number,
  theirGoals: number,
): CareerState {
  const fixture = nextCareerFixture(state);
  if (!fixture) return state;
  const weAreHome = fixture.home === state.clubId;
  return advanceCareerRound(state, {
    homeGoals: weAreHome ? ourGoals : theirGoals,
    awayGoals: weAreHome ? theirGoals : ourGoals,
  });
}

/**
 * Close a finished season into `history` and start the next one. Kept as an
 * explicit action rather than firing on the last whistle so the player gets to
 * look at the final table first.
 */
export function startNextSeason(state: CareerState): CareerState {
  if (!careerSeasonComplete(state)) return state;
  const table = careerTable(state);
  const mine = table.find((r) => r.teamId === state.clubId);
  const record: SeasonRecord = {
    season: state.season,
    position: positionInTable(table, state.clubId),
    points: mine?.points ?? 0,
    played: mine?.played ?? 0,
  };
  return {
    ...state,
    season: state.season + 1,
    round: 0,
    results: [],
    // Re-seed, or every season would be a replay of the last one.
    seed: hashString(`${state.clubId}:career:${state.season + 1}`),
    history: [...state.history, record],
  };
}

// ---------------------------------------------------------------------------
// World Cup
// ---------------------------------------------------------------------------

export type WorldCupStage = 'groups' | 'knockout' | 'done';

export interface WorldCupState {
  version: 1;
  nationId: string;
  seed: number;
  /** The draw, frozen at creation. */
  groups: Group[];
  groupResults: MatchResult[];
  /** 0-based group matchday; `=== groupRoundCount` means the groups are done. */
  groupRound: number;
  /** Empty until the group stage finishes, then 8 + 4 + 2 + 1 ties. */
  bracket: BracketMatch[];
  /** 0-based knockout round (0 = R16). */
  knockoutRound: number;
  champion: string | null;
}

/**
 * The 32 who make it, in rank order, with `nationId` guaranteed a place.
 *
 * Qualifying is the one thing this mode does NOT simulate — you picked a
 * nation to play as, and being told it failed to qualify before you kicked a
 * ball would be a worse game than the small untruth of an automatic place. The
 * strongest of the rest fill the remaining slots, so the field you beat is
 * still the best field available. Returned in rank order because drawGroups
 * reads its pots off the front of the list.
 */
export function qualifyField(nationId: string): string[] {
  const ranked = NATIONS.slice()
    .sort((a, b) => b.rating - a.rating || (a.id < b.id ? -1 : 1))
    .map((n) => n.id);
  const rank = new Map(ranked.map((id, i) => [id, i]));
  if (!rank.has(nationId)) return ranked.slice(0, FIELD_SIZE);
  return ranked
    .filter((id) => id !== nationId)
    .slice(0, FIELD_SIZE - 1)
    .concat(nationId)
    .sort((a, b) => (rank.get(a) ?? 0) - (rank.get(b) ?? 0));
}

export function startWorldCup(nationId: string): WorldCupState {
  const seed = hashString(`${nationId}:worldcup`);
  // Rank by rating so the pots are real pots (drawGroups' header explains why a
  // flat shuffle produces nonsense groups).
  const ranked = qualifyField(nationId);
  return {
    version: 1,
    nationId,
    seed,
    groups: drawGroups(ranked, GROUP_COUNT, createRng(seed)),
    groupResults: [],
    groupRound: 0,
    bracket: [],
    knockoutRound: 0,
    champion: null,
  };
}

export function worldCupFixtures(state: WorldCupState): Fixture[] {
  return groupStageFixtures(state.groups);
}

export function worldCupStage(state: WorldCupState): WorldCupStage {
  if (state.champion) return 'done';
  return state.bracket.length === 0 ? 'groups' : 'knockout';
}

export function groupOf(state: WorldCupState, teamId: string): Group | null {
  return state.groups.find((g) => g.teamIds.includes(teamId)) ?? null;
}

export function groupStandings(state: WorldCupState, group: Group): TableRow[] {
  return computeTable(group.teamIds, state.groupResults);
}

/** The player's next match, or null when they are out / the round is theirs to
 *  simulate only. `kind` tells the screen which record function to call. */
export type NextWorldCupMatch =
  | { kind: 'group'; fixture: Fixture; token: string }
  | { kind: 'knockout'; match: BracketMatch; token: string }
  | null;

export function nextWorldCupMatch(state: WorldCupState): NextWorldCupMatch {
  if (worldCupStage(state) === 'groups') {
    if (state.groupRound >= groupRoundCount(state.groups)) return null;
    const fixture = fixtureFor(worldCupFixtures(state), state.groupRound, state.nationId);
    if (!fixture) return null;
    return { kind: 'group', fixture, token: `wc:g${state.groupRound}` };
  }
  if (worldCupStage(state) === 'knockout') {
    const match = bracketMatchFor(state.bracket, state.knockoutRound, state.nationId);
    if (!match || match.winner !== null || !match.home || !match.away) return null;
    return { kind: 'knockout', match, token: `wc:k${match.id}` };
  }
  return null;
}

export function worldCupMatchToken(state: WorldCupState): string {
  return nextWorldCupMatch(state)?.token ?? '';
}

function wcRng(state: WorldCupState, tag: string): () => number {
  return createRng(state.seed ^ hashString(tag));
}

/**
 * Advance one round of the tournament — group matchday or knockout round.
 * `ourResult` is the human's scoreline in HOME/AWAY terms, or null to simulate.
 *
 * Group → knockout is a transition, not a special case the caller handles: once
 * the last group matchday is recorded, the bracket is built from the standings
 * in the same call, so `worldCupStage` flips on its own.
 */
export function advanceWorldCupRound(
  state: WorldCupState,
  ourResult: { homeGoals: number; awayGoals: number } | null,
): WorldCupState {
  const stage = worldCupStage(state);
  if (stage === 'done') return state;

  if (stage === 'groups') {
    const fixtures = worldCupFixtures(state);
    const round = state.groupRound;
    const rng = wcRng(state, `g${round}`);
    const ours = fixtureFor(fixtures, round, state.nationId);
    const played: MatchResult[] = [];
    if (ours) {
      const score = ourResult ?? simulateResult(ratingOf(ours.home), ratingOf(ours.away), rng);
      played.push({
        round, home: ours.home, away: ours.away,
        homeGoals: score.homeGoals, awayGoals: score.awayGoals,
        played: ourResult !== null,
      });
    }
    const rest = simulateRound(
      fixtures, round, ratingOf, rng,
      (f) => !!ours && f.home === ours.home && f.away === ours.away,
    );
    const groupResults = [...state.groupResults, ...played, ...rest];
    const nextRound = round + 1;
    const groupsDone = nextRound >= groupRoundCount(state.groups);
    return {
      ...state,
      groupResults,
      groupRound: nextRound,
      bracket: groupsDone
        ? buildBracket(qualifiersFromGroups(state.groups, groupResults))
        : state.bracket,
      knockoutRound: 0,
    };
  }

  // Knockout.
  const round = state.knockoutRound;
  const rng = wcRng(state, `k${round}`);
  const ours = bracketMatchFor(state.bracket, round, state.nationId);
  let bracket = state.bracket;
  if (ours && ourResult && ours.winner === null) {
    bracket = setBracketResult(bracket, ours.id, ourResult.homeGoals, ourResult.awayGoals, rng);
  }
  bracket = simulateBracketRound(bracket, round, ratingOf, rng, null);
  const lastRound = bracketRoundCount(bracket) - 1;
  const done = bracketComplete(bracket);
  return {
    ...state,
    bracket,
    knockoutRound: done ? lastRound : Math.min(round + 1, lastRound),
    champion: done ? bracketChampion(bracket) : null,
  };
}

/** Record the human's World Cup scoreline (their goals first). */
export function recordWorldCupResult(
  state: WorldCupState,
  ourGoals: number,
  theirGoals: number,
): WorldCupState {
  const next = nextWorldCupMatch(state);
  if (!next) return state;
  const home = next.kind === 'group' ? next.fixture.home : next.match.home;
  const weAreHome = home === state.nationId;
  return advanceWorldCupRound(state, {
    homeGoals: weAreHome ? ourGoals : theirGoals,
    awayGoals: weAreHome ? theirGoals : ourGoals,
  });
}

/** True when the player's nation can no longer win it (group exit or a loss). */
export function worldCupEliminated(state: WorldCupState): boolean {
  // Champion first: once it is set `worldCupStage` reports 'done', so testing
  // the stage before this makes a finished tournament always claim we are still
  // in it — the runner-up would never be told they lost the final.
  if (state.champion) return state.champion !== state.nationId;
  if (worldCupStage(state) !== 'knockout') return false;
  /*
   * Only rounds we have REACHED count. Without the round guard a nation that
   * won its Round of 16 stayed "alive" for the rest of the tournament, because
   * that historic win still satisfies `winner === nationId` — so a side knocked
   * out in the quarter-finals was never reported as eliminated, and the UI had
   * no way to tell you your run was over.
   */
  const alive = state.bracket.some(
    (m) =>
      m.round >= state.knockoutRound &&
      (m.home === state.nationId || m.away === state.nationId) &&
      (m.winner === null || m.winner === state.nationId),
  );
  return !alive;
}

/** Ties in the current knockout round, for the bracket view. */
export function currentKnockoutTies(state: WorldCupState): BracketMatch[] {
  return bracketRound(state.bracket, state.knockoutRound);
}


// ---------------------------------------------------------------------------
// Honours — the trophy cabinet
// ---------------------------------------------------------------------------

/**
 * What the player has actually WON, kept forever.
 *
 * A tournament you can finish and then have nothing to show for is a tournament
 * you finish once. This is the only state in the game that never resets: careers
 * and World Cups are wiped and restarted constantly, and the cabinet is what
 * survives them — the reason to enter the next one.
 *
 * Deliberately small. A cabinet with forty counters is a spreadsheet; a cabinet
 * with five is a shelf you want to fill.
 */
export interface Honours {
  version: 1;
  /**
   * Events already counted, so a trophy cannot be banked twice.
   *
   * The first guard for this was a variable inside the menu's closure, which
   * survives re-renders but NOT a remount — and the menu remounts every time a
   * match returns to it. The result was a cabinet reading "Title number 5" in
   * SEASON ONE, because every visit to the season-end screen banked the same
   * title again. A counter that can be inflated by navigation is not a record
   * of anything, so the guard has to live where the counters live.
   */
  claimed: string[];
  /** World Cups lifted. */
  worldCups: number;
  /** League titles won (1st place at the end of a season). */
  leagueTitles: number;
  /** Seasons completed, won or not. */
  seasonsPlayed: number;
  /** Tournaments entered, finished or abandoned. */
  tournamentsEntered: number;
  /** Furthest knockout round ever reached: 0=R16 … 3=Final, -1 = never. */
  bestWorldCupRound: number;
  /** Best league finish ever, 1-based. 0 = never finished a season. */
  bestLeaguePosition: number;
}

export function defaultHonours(): Honours {
  return {
    version: 1,
    claimed: [],
    worldCups: 0,
    leagueTitles: 0,
    seasonsPlayed: 0,
    tournamentsEntered: 0,
    bestWorldCupRound: -1,
    bestLeaguePosition: 0,
  };
}

export function loadHonours(): Honours {
  try {
    const raw = localStorage.getItem(HONOURS_KEY);
    if (!raw) return defaultHonours();
    const parsed = JSON.parse(raw) as Partial<Honours>;
    // Merge over the defaults so a cabinet written by an older build, missing
    // a counter added since, loads rather than throwing the lot away.
    const merged = {
      ...defaultHonours(),
      ...parsed,
      claimed: Array.isArray(parsed.claimed) ? parsed.claimed : [],
      version: 1 as const,
    };
    /*
     * REPAIR counts the pre-`claimed` bug inflated.
     *
     * Before the claimed-key guard existed, every visit to a season-end screen
     * re-banked the season, so live cabinets read things like "Title number 8"
     * in season one. These invariants are true by definition — you cannot have
     * won more leagues than you have finished seasons, nor more World Cups than
     * you have entered — so clamping is a safe self-heal rather than a guess,
     * and it spares the player having to wipe their data (and their coins) to
     * get an honest number back.
     */
    merged.seasonsPlayed = Math.max(0, Math.floor(merged.seasonsPlayed));
    merged.leagueTitles = Math.min(Math.max(0, Math.floor(merged.leagueTitles)), merged.seasonsPlayed);
    merged.tournamentsEntered = Math.max(0, Math.floor(merged.tournamentsEntered));
    merged.worldCups = Math.min(
      Math.max(0, Math.floor(merged.worldCups)),
      Math.max(merged.tournamentsEntered, 0),
    );
    return merged;
  } catch {
    return defaultHonours();
  }
}

export function saveHonours(h: Honours): void {
  try {
    localStorage.setItem(HONOURS_KEY, JSON.stringify(h));
  } catch {
    /* private mode / quota — the cabinet is a nicety, never a hard failure */
  }
}

export function recordHonours(patch: Partial<Honours>): Honours {
  const next = { ...loadHonours(), ...patch, version: 1 as const };
  saveHonours(next);
  return next;
}

/**
 * Note a World Cup result. Safe to call repeatedly — keyed on the tournament,
 * so navigating back to a finished run cannot re-award it.
 */
export function recordWorldCupHonours(state: WorldCupState): Honours {
  const h = loadHonours();
  const key = `wc:${state.seed}`;
  if (h.claimed.includes(key)) return h;
  const won = state.champion === state.nationId;
  // How far we actually got: the last round containing one of our ties.
  let best = -1;
  for (const m of state.bracket) {
    if (m.home === state.nationId || m.away === state.nationId) best = Math.max(best, m.round);
  }
  return recordHonours({
    worldCups: h.worldCups + (won ? 1 : 0),
    bestWorldCupRound: Math.max(h.bestWorldCupRound, won ? 3 : best),
    tournamentsEntered: h.tournamentsEntered,
    claimed: [...h.claimed, key],
  });
}

/**
 * Note a completed league season. Safe to call repeatedly — `key` identifies the
 * season, so revisiting the end-of-season screen cannot re-award the title.
 */
export function recordSeasonHonours(position: number, key: string): Honours {
  const h = loadHonours();
  if (h.claimed.includes(key)) return h;
  return recordHonours({
    seasonsPlayed: h.seasonsPlayed + 1,
    leagueTitles: h.leagueTitles + (position === 1 ? 1 : 0),
    bestLeaguePosition:
      h.bestLeaguePosition === 0 ? position : Math.min(h.bestLeaguePosition, position),
    claimed: [...h.claimed, key],
  });
}

// ---------------------------------------------------------------------------
// Squads (formation + starting XI, per team)
// ---------------------------------------------------------------------------

export interface SquadSetup {
  formationId: string;
  /** 11 player ids, index = formation slot. '' = an empty slot. */
  lineup: string[];
}

export interface SquadsState {
  version: 1;
  teams: Record<string, SquadSetup>;
}

export function emptySquads(): SquadsState {
  return { version: 1, teams: {} };
}

/** The team's saved setup, or a freshly auto-picked XI. Never returns a lineup
 *  of the wrong length — the squad screen indexes it by formation slot. */
export function setupFor(state: SquadsState, teamId: string): SquadSetup {
  const saved = state.teams[teamId];
  const formationId = saved?.formationId ?? DEFAULT_FORMATION_ID;
  const slots = formationById(formationId).slots.length;
  if (saved && saved.lineup.length === slots) return saved;
  return { formationId, lineup: autoLineup(teamId, formationId) };
}

/**
 * Switch formation, keeping the XI. The same eleven players are re-fitted to
 * the new shape by `autoLineup` restricted to them — a manager who moves 4-4-2
 * to 3-5-2 expects their eleven to rearrange, not to be replaced.
 */
export function withFormation(state: SquadsState, teamId: string, formationId: string): SquadsState {
  const current = setupFor(state, teamId);
  const keep = new Set(current.lineup.filter(Boolean));
  const formation = formationById(formationId);
  const squad = squadFor(teamId);
  const used = new Set<string>();
  const lineup = formation.slots.map((slotPos) => {
    let bestId = '';
    let bestScore = -1;
    for (const p of squad) {
      if (used.has(p.id) || !keep.has(p.id)) continue;
      const score = positionFit(p.position, slotPos) * 1000 + p.rating;
      if (score > bestScore) {
        bestScore = score;
        bestId = p.id;
      }
    }
    if (bestId) used.add(bestId);
    return bestId;
  });
  // Any slot the retained XI could not fill (shouldn't happen with 11 kept,
  // but a corrupt blob can) falls back to the best unused squad member.
  for (let i = 0; i < lineup.length; i++) {
    if (lineup[i]) continue;
    const spare = squad.find((p) => !used.has(p.id) && !lineup.includes(p.id));
    if (spare) {
      lineup[i] = spare.id;
      used.add(spare.id);
    }
  }
  return { ...state, teams: { ...state.teams, [teamId]: { formationId, lineup } } };
}

/**
 * Put `playerId` in `slot`. If they were already in the XI the two players
 * SWAP; otherwise whoever held the slot drops to the bench. That swap rule is
 * what makes the pitch view feel like FIFA's — click two shirts, they exchange.
 */
export function withAssignment(
  state: SquadsState,
  teamId: string,
  slot: number,
  playerId: string,
): SquadsState {
  const current = setupFor(state, teamId);
  if (slot < 0 || slot >= current.lineup.length) return state;
  const lineup = current.lineup.slice();
  const existingSlot = lineup.indexOf(playerId);
  const displaced = lineup[slot] ?? '';
  lineup[slot] = playerId;
  if (existingSlot >= 0 && existingSlot !== slot) lineup[existingSlot] = displaced;
  return {
    ...state,
    teams: { ...state.teams, [teamId]: { formationId: current.formationId, lineup } },
  };
}

/** Throw away manual edits and re-pick the XI by rating and fit. */
export function withAutoLineup(state: SquadsState, teamId: string): SquadsState {
  const current = setupFor(state, teamId);
  return {
    ...state,
    teams: {
      ...state.teams,
      [teamId]: {
        formationId: current.formationId,
        lineup: autoLineup(teamId, current.formationId),
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Parse / serialize (never throws — see the header)
// ---------------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function parseResults(v: unknown): MatchResult[] {
  if (!Array.isArray(v)) return [];
  const out: MatchResult[] = [];
  for (const raw of v) {
    if (!isRecord(raw)) continue;
    const home = str(raw['home']);
    const away = str(raw['away']);
    if (!home || !away) continue;
    out.push({
      round: num(raw['round'], 0),
      home,
      away,
      homeGoals: Math.max(0, Math.round(num(raw['homeGoals'], 0))),
      awayGoals: Math.max(0, Math.round(num(raw['awayGoals'], 0))),
      played: raw['played'] === true,
    });
  }
  return out;
}

function parseIds(v: unknown): string[] {
  return Array.isArray(v) ? v.map(str).filter(Boolean) : [];
}


/**
 * Every team id in a save must still exist in the shipped dataset.
 *
 * The World Cup nation list changed from 32 invented countries to 32 real ones,
 * so a save written before that change references ids that no longer resolve.
 * Without this check the save still PARSES — the ids are only strings — and the
 * tournament renders stub teams with blank flags and no ratings, which looks
 * like a bug rather than an out-of-date save. Returning null instead makes the
 * hub offer a clean start, which is the honest outcome.
 */
function allTeamsExist(ids: readonly string[]): boolean {
  return ids.every((id) => teamById(id) !== undefined);
}

export function parseCareer(raw: string | null): CareerState | null {
  if (!raw) return null;
  try {
    const data: unknown = JSON.parse(raw);
    if (!isRecord(data)) return null;
    const clubId = str(data['clubId']);
    const clubIds = parseIds(data['clubIds']);
    // An empty or odd club list cannot produce a schedule; treat it as no career
    // rather than letting roundRobin silently drop a team mid-season.
    if (!clubId || clubIds.length < 2 || clubIds.length % 2 !== 0) return null;
    if (!clubIds.includes(clubId)) return null;
    // Saved club ids must still be real clubs (see allTeamsExist).
    if (!allTeamsExist(clubIds)) return null;
    const history: SeasonRecord[] = Array.isArray(data['history'])
      ? data['history'].filter(isRecord).map((h) => ({
          season: num(h['season'], 1),
          position: num(h['position'], 0),
          points: num(h['points'], 0),
          played: num(h['played'], 0),
        }))
      : [];
    const state: CareerState = {
      version: 1,
      clubId,
      seed: num(data['seed'], hashString(clubId)) >>> 0,
      season: Math.max(1, Math.round(num(data['season'], 1))),
      round: Math.max(0, Math.round(num(data['round'], 0))),
      clubIds,
      results: parseResults(data['results']),
      history,
    };
    state.round = Math.min(state.round, careerTotalRounds(state));
    return state;
  } catch {
    return null;
  }
}

function parseGroups(v: unknown): Group[] {
  if (!Array.isArray(v)) return [];
  const out: Group[] = [];
  for (const raw of v) {
    if (!isRecord(raw)) continue;
    const teamIds = parseIds(raw['teamIds']);
    if (teamIds.length < 2) continue;
    out.push({ name: str(raw['name']) || String(out.length + 1), teamIds });
  }
  return out;
}

function parseBracket(v: unknown): BracketMatch[] {
  if (!Array.isArray(v)) return [];
  const out: BracketMatch[] = [];
  for (const raw of v) {
    if (!isRecord(raw)) continue;
    const id = str(raw['id']);
    if (!id) continue;
    const shootout = raw['shootout'];
    out.push({
      id,
      round: Math.max(0, Math.round(num(raw['round'], 0))),
      slot: Math.max(0, Math.round(num(raw['slot'], 0))),
      home: str(raw['home']) || null,
      away: str(raw['away']) || null,
      homeGoals: typeof raw['homeGoals'] === 'number' ? raw['homeGoals'] : null,
      awayGoals: typeof raw['awayGoals'] === 'number' ? raw['awayGoals'] : null,
      shootout:
        Array.isArray(shootout) && shootout.length === 2
          ? [num(shootout[0], 0), num(shootout[1], 0)]
          : null,
      winner: str(raw['winner']) || null,
    });
  }
  return out;
}

export function parseWorldCup(raw: string | null): WorldCupState | null {
  if (!raw) return null;
  try {
    const data: unknown = JSON.parse(raw);
    if (!isRecord(data)) return null;
    const nationId = str(data['nationId']);
    const groups = parseGroups(data['groups']);
    if (!nationId || groups.length === 0) return null;
    if (!groups.some((g) => g.teamIds.includes(nationId))) return null;
    // Saved nation ids must still be real nations. This is what rejects a save
    // written against the previous invented-country dataset.
    if (!allTeamsExist(groups.flatMap((g) => g.teamIds))) return null;
    const bracket = parseBracket(data['bracket']);
    const state: WorldCupState = {
      version: 1,
      nationId,
      seed: num(data['seed'], hashString(nationId)) >>> 0,
      groups,
      groupResults: parseResults(data['groupResults']),
      groupRound: Math.max(0, Math.round(num(data['groupRound'], 0))),
      bracket,
      knockoutRound: Math.max(0, Math.round(num(data['knockoutRound'], 0))),
      champion: str(data['champion']) || null,
    };
    state.groupRound = Math.min(state.groupRound, groupRoundCount(groups));
    state.knockoutRound = Math.min(state.knockoutRound, Math.max(0, bracketRoundCount(bracket) - 1));
    return state;
  } catch {
    return null;
  }
}

export function parseSquads(raw: string | null): SquadsState {
  if (!raw) return emptySquads();
  try {
    const data: unknown = JSON.parse(raw);
    if (!isRecord(data) || !isRecord(data['teams'])) return emptySquads();
    const teams: Record<string, SquadSetup> = {};
    for (const [teamId, value] of Object.entries(data['teams'])) {
      if (!isRecord(value)) continue;
      const formationId = str(value['formationId']) || DEFAULT_FORMATION_ID;
      const lineup = Array.isArray(value['lineup']) ? value['lineup'].map(str) : [];
      // Length must match the formation or the pitch view would index past the
      // end; a mismatch is dropped so setupFor() re-derives a valid XI.
      if (lineup.length !== formationById(formationId).slots.length) continue;
      teams[teamId] = { formationId, lineup };
    }
    return { version: 1, teams };
  } catch {
    return emptySquads();
  }
}

// ---------------------------------------------------------------------------
// Storage (guarded — node tests and private mode have no localStorage)
// ---------------------------------------------------------------------------

function readKey(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Quota / private-mode failures must never take the menu down.
  }
}

export function loadCareer(): CareerState | null {
  return parseCareer(readKey(CAREER_KEY));
}
export function saveCareer(state: CareerState): void {
  writeKey(CAREER_KEY, JSON.stringify(state));
}
export function clearCareer(): void {
  writeKey(CAREER_KEY, null);
}

export function loadWorldCup(): WorldCupState | null {
  return parseWorldCup(readKey(WORLD_CUP_KEY));
}
export function saveWorldCup(state: WorldCupState): void {
  writeKey(WORLD_CUP_KEY, JSON.stringify(state));
}
export function clearWorldCup(): void {
  writeKey(WORLD_CUP_KEY, null);
}

export function loadSquads(): SquadsState {
  return parseSquads(readKey(SQUADS_KEY));
}
export function saveSquads(state: SquadsState): void {
  writeKey(SQUADS_KEY, JSON.stringify(state));
}

// ---------------------------------------------------------------------------
// The match ⇄ competition seam
// ---------------------------------------------------------------------------

/**
 * Tagged onto a `MatchSetup` when Career or World Cup starts a match through
 * the ordinary practice handler. main.ts reads it back on exit — see the header
 * for why the token exists.
 */
export interface CompetitionContext {
  kind: 'career' | 'worldCup';
  /** 'ARENA LEAGUE · MATCHDAY 4' — shown on the VS card and full-time board. */
  label: string;
  /** The human's team (the sim always seats them as team A). */
  teamALabel: string;
  teamBLabel: string;
  token: string;
}

/**
 * Write a played scoreline back into the persisted competition.
 *
 * Returns true if it was recorded. A false means the token no longer matches
 * what is stored — the career was reset, the tournament advanced in another
 * tab, or the match was started before a reset — and the result was DROPPED,
 * which is the only safe thing to do with a scoreline that has no fixture.
 */
export function reportCompetitionResult(
  ctx: CompetitionContext,
  ourGoals: number,
  theirGoals: number,
): boolean {
  if (ctx.kind === 'career') {
    const state = loadCareer();
    if (!state || careerMatchToken(state) !== ctx.token) return false;
    saveCareer(recordCareerResult(state, ourGoals, theirGoals));
    return true;
  }
  const state = loadWorldCup();
  if (!state || worldCupMatchToken(state) !== ctx.token) return false;
  saveWorldCup(recordWorldCupResult(state, ourGoals, theirGoals));
  return true;
}
